import { api, expectBody } from '../../api/client.js';

/**
 * The sign-in surface, as HTTP.
 *
 * Nothing here touches the session: these call `/v1/auth` and return what it said. Turning an
 * answer into a signed-in workspace — the access token in memory, the refresh timer, the socket —
 * is `src/session/session.ts`, so that the transport can be read without the lifecycle and the
 * lifecycle can be tested without a server.
 *
 * Every call sends its cookies. For a deployment they are same-origin anyway; for a split-origin
 * development setup `include` is what lets the refresh cookie reach `/v1/auth`, and it is the only
 * place in the workspace that needs it — the cookie is scoped to that path precisely so the
 * hundreds of other calls a session makes cannot carry it.
 */
const withCredentials = { credentials: 'include' } as const;

/** What a sign-in or a refresh answered with. The refresh token itself is not in here: it is a cookie. */
export interface IssuedAccess {
  readonly accessToken: string;
  readonly expiresIn: number;
}

/** Whether this address signs in with a password or through a provider, and which. */
export type HomeRealm =
  | { readonly method: 'password' }
  | {
      readonly method: 'oidc';
      readonly providerId: string;
      readonly providerName: string;
      readonly ssoOnly: boolean;
    };

/**
 * Home-realm discovery.
 *
 * It answers the same way for an address nobody here uses as for one somebody does, so the sign-in
 * page can ask about any address without the answer enumerating a tenant's staff.
 */
export async function discoverHomeRealm(email: string): Promise<HomeRealm> {
  return expectBody(await api.POST('/v1/auth/discover', { body: { email }, ...withCredentials }));
}

export async function signInWithPassword(email: string, password: string): Promise<IssuedAccess> {
  const issued = expectBody(
    await api.POST('/v1/auth/login', {
      // `browser` is the default server-side, and saying it is what puts the refresh token in an
      // HttpOnly cookie rather than in a body this script could read.
      body: { email, password, clientType: 'browser' },
      ...withCredentials,
    }),
  );
  return { accessToken: issued.accessToken, expiresIn: issued.expiresIn };
}

/**
 * Accepts an invitation, which is **the only way a user comes to exist in a tenant**.
 *
 * Provisioning writes an invitation and emails a code; nothing else creates the first account, so
 * until this screen existed a tenant's owner had to call the API by hand on a deployment where
 * every other step was a page. The answer is the user and **no session** — the API issues nothing
 * here — so the page signs in afterwards with the password just chosen.
 */
export async function acceptInvitation(input: {
  readonly token: string;
  readonly displayName: string;
  readonly password: string;
}): Promise<{ readonly email: string }> {
  const user = expectBody(
    await api.POST('/v1/auth/invitations/accept', { body: input, ...withCredentials }),
  );
  return { email: user.email };
}

/**
 * Begins a federated sign-in and answers with where to send the person.
 *
 * `returnTo` is where the workspace wants to end up; the domain refuses anything that is not a path
 * inside it.
 */
export async function startOidcSignIn(input: {
  readonly email?: string;
  readonly providerId?: string;
  readonly returnTo?: string;
}): Promise<{ readonly authorizationUrl: string }> {
  const begun = expectBody(
    await api.POST('/v1/auth/oidc/start', {
      body: { ...input, clientType: 'browser' },
      ...withCredentials,
    }),
  );
  return { authorizationUrl: begun.authorizationUrl };
}

/**
 * Rotates the refresh cookie for a fresh access token.
 *
 * The body is empty on purpose: the browser's refresh token is a cookie it cannot read, and the
 * route accepts both because a native client sends the token it holds instead.
 */
export async function refreshAccess(): Promise<IssuedAccess> {
  const issued = expectBody(await api.POST('/v1/auth/refresh', { body: {}, ...withCredentials }));
  return { accessToken: issued.accessToken, expiresIn: issued.expiresIn };
}

/** Ends this session. Other devices keep theirs. */
export async function signOutOfSession(): Promise<void> {
  await api.POST('/v1/auth/logout', { body: {}, ...withCredentials });
}
